import React, { useState } from 'react';
import { FileText, ShieldCheck, TriangleAlert } from 'lucide-react';

import RenduDocument from './RenduDocument';
import type { Citation } from '../../services/api/assistants';

interface Props {
  reponse: string;
  citations: Citation[];
  horsCorpus?: boolean;
}

/**
 * Réponse fondée sur les documents déposés, avec ses pièces justificatives.
 *
 * Les citations ne sont pas un ornement : chaque passage affiché ici a été
 * confronté au document avant d'arriver à l'écran. Les montrer permet à l'agent
 * de vérifier lui-même, ce qui est la condition pour qu'une réponse serve dans
 * un rapport officiel.
 */
const RenduCarnet: React.FC<Props> = ({ reponse, citations, horsCorpus }) => {
  const [depliee, setDepliee] = useState<number | null>(null);

  if (horsCorpus) {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-3 rounded-md border border-eg-alerte/30 bg-eg-alerte/10 p-4">
          <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-eg-alerte" />
          <div>
            <p className="font-medium text-eg-encre">
              Les documents déposés ne permettent pas de répondre
            </p>
            <p className="mt-1 text-sm text-eg-alerte">
              L'assistant s'en tient aux pièces de l'activité plutôt que de combler
              le manque.
            </p>
          </div>
        </div>
        <RenduDocument contenu={reponse} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <RenduDocument contenu={reponse} />

      {citations.length > 0 && (
        <div className="rounded-md border border-eg-bord bg-eg-papier p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-eg-brun">
            <ShieldCheck className="h-4 w-4 text-eg-accent" />
            {citations.length} passage{citations.length > 1 ? 's' : ''} retrouvé
            {citations.length > 1 ? 's' : ''} dans les documents
          </div>

          <ul className="space-y-2">
            {citations.map((citation, index) => {
              const ouverte = depliee === index;
              return (
                <li key={index}>
                  <button
                    type="button"
                    onClick={() => setDepliee(ouverte ? null : index)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left
                      text-sm transition-colors hover:bg-eg-blanc"
                  >
                    <span
                      className="shrink-0 rounded bg-eg-ombre px-1.5 py-0.5 text-xs
                        font-semibold text-eg-blanc"
                    >
                      S{citation.source}
                    </span>
                    <FileText className="h-3.5 w-3.5 shrink-0 text-eg-estompe" />
                    <span className="truncate text-eg-brun">
                      {citation.document || `Source ${citation.source}`}
                    </span>
                  </button>

                  {ouverte && (
                    <blockquote
                      className="ml-8 mt-1 border-l-2 border-eg-bord bg-eg-blanc px-3 py-2
                        text-sm italic text-eg-gris"
                    >
                      {citation.extrait}
                    </blockquote>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RenduCarnet;
